<template>
  <div class="page-container">
    <el-row type="flex" justify="space-between" align="middle">
      <div style="display: flex; align-items: center; gap: 18px">
        <h2>Docutax Submissions</h2>
        <el-button :disabled="refreshing" size="small" icon="el-icon-refresh" circle @click="onRefresh" />
      </div>
      <el-button size="small" type="primary" icon="el-icon-s-promotion" @click="onSendNewRequestClick">
        New Docutax Request
      </el-button>
    </el-row>
    <el-row type="flex" align="middle" justify="start">
      <el-checkbox-group size="small" v-model="filters.statuses">
        <el-checkbox-button v-for="status in Object.values($options.DOCUTAX_SUBMISSION_STATUS)" :label="status"
          :key="status">
          {{ $options.DOCUTAX_SUBMISSION_STATUS_DESCRIPTION_MAP[status] }}
        </el-checkbox-button>
      </el-checkbox-group>
      <el-checkbox class="ml-3" v-model="filters.assignedToMe">Assigned to me</el-checkbox>
      <el-checkbox class="ml-3" v-model="filters.showAllOfMyOffices">Show All of my Offices</el-checkbox>
      <div style="display: flex; flex-grow: 1">&nbsp;</div>
      <el-input style="max-width: 250px" size="small" placeholder="Search" v-model="filters.query">
        <el-button slot="append" icon="el-icon-search"></el-button>
      </el-input>
    </el-row>
    <el-alert class="mt-3 mb-3" v-if="!activeOffice && !filters.showAllOfMyOffices" type="warning"
      title="Please select an office or check 'Show All of my Offices' to continue." show-icon />
    <div v-else>
      <docutax-submissions-table v-loading="loading" :submissions="searchResultPage.content"
        @sendRequest="onSendRequestToExistingClient" @rowUpdated="onRowUpdate" />
      <el-row class="mt-3" type="flex" justify="center">
        <el-pagination layout="total, prev, pager, next" :current-page="filters.page + 1"
          @update:currentPage="page => filters.page = page - 1" :page-size="filters.pageSize"
          :total="searchResultPage.totalElements">
        </el-pagination>
      </el-row>
    </div>
    <el-dialog title="New Docutax Request" :visible.sync="requestDialogVm.visible">
      <docutax-request-dialog :docutax-client-id.sync="requestDialogVm.docutaxClientId"
        @close="requestDialogVm.visible = false" />
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import debounce from 'lodash/debounce';
import api from '@/api';
import DocutaxSubmissionsTable from '@/components/tables/DocutaxSubmissions.vue';
import DocutaxRequestDialog from '@/components/dialog-content/DocutaxRequest.vue';
import { DOCUTAX_SUBMISSION_STATUS, DOCUTAX_SUBMISSION_STATUS_DESCRIPTION_MAP } from '@/enum/DocutaxSubmissionStatus';
import DocutaxSubmissionSearchResultPage from '@/model/DocutaxSubmissionSearchResultPage';

export default {
  components: {
    DocutaxRequestDialog,
    DocutaxSubmissionsTable,
  },
  DOCUTAX_SUBMISSION_STATUS,
  DOCUTAX_SUBMISSION_STATUS_DESCRIPTION_MAP,
  data() {
    return {
      filters: {
        query: '',
        statuses: [DOCUTAX_SUBMISSION_STATUS.NEW, DOCUTAX_SUBMISSION_STATUS.IN_PROGRESS],
        assignedToMe: false,
        showAllOfMyOffices: false,
        page: 0,
        pageSize: 20,
        sortField: 'created',
        sortDirection: 'DESC',
      },
      requestDialogVm: {
        visible: false,
        docutaxClientId: undefined,
      },
      loading: false,
      refreshing: false,
      searchResultPage: new DocutaxSubmissionSearchResultPage(),
    };
  },
  watch: {
    filters: {
      immediate: true,
      deep: true,
      handler() {
        this.performSearchDebounced();
      },
    },
    activeOffice: {
      handler() {
        this.performSearchDebounced();
      },
    },
  },
  computed: {
    ...mapGetters('User', ['activeOffice']),
  },
  methods: {
    performSearchDebounced: debounce(function search() { this.performSearch(); }, 300),
    async performSearch() {
      try {
        this.loading = true;
        const response = await api.docutaxSubmissions.search(this.filters);
        if (response.data) {
          this.searchResultPage = new DocutaxSubmissionSearchResultPage(response.data);
        }
      } catch (e) {
        this.$message.error('Failed to load Docutax submissions', e);
      } finally {
        this.loading = false;
      }
    },
    onRowUpdate(newRow) {
      const index = this.searchResultPage.content.findIndex(oldRow => oldRow.id === newRow.id);
      if (index > -1) {
        this.searchResultPage.content.splice(index, 1, newRow);
      }
    },
    onSendRequestToExistingClient(row) {
      this.requestDialogVm.visible = true;
      this.requestDialogVm.docutaxClientId = row.clientId;
    },
    onSendNewRequestClick() {
      this.requestDialogVm.visible = true;
      this.requestDialogVm.docutaxClientId = undefined;
    },
    async onRefresh() {
      try {
        this.refreshing = true;
        await this.performSearch();
      } finally {
        this.refreshing = false;
      }
    },
  },
};

</script>

<style lang="scss" scoped>

</style>
